import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { retryAfterMs, toFailure } from '@cedarreachai/api-client';
import { recordPauseObservation } from '@/features/portfolio/pauseState';
import { env } from '@/lib/env';

/** Production backoff before retry `attempt` (0-based): 1 s, 2 s, … capped at 8 s. */
export function productionRetryDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 8000);
}

/**
 * Backoff against the in-browser mock backend: 50 ms, 100 ms, … capped at 400 ms.
 *
 * Only the *delay* shortens. Which failures are retried, and how many times,
 * is the production policy unchanged, so an error-state spec still walks the
 * real retry path before the error renders; it just does not wait 3 s of
 * wall-clock for a mock that answers the same way every time.
 */
export function mockBackendRetryDelay(attempt: number): number {
  return Math.min(50 * 2 ** attempt, 400);
}

/**
 * The wait before retry `attempt` after `error`: the backoff, or the server's
 * `Retry-After` when a `429` names a longer one (rounded up by
 * `retryAfterMs`), so a retry never arrives before the server would accept
 * it — against the mock backend too.
 */
export function honouringRetryAfter(
  backoff: (attempt: number) => number,
): (attempt: number, error: unknown) => number {
  return (attempt, error) => Math.max(backoff(attempt), retryAfterMs(toFailure(error)) ?? 0);
}

export interface QueryClientOptions {
  /**
   * Whether the app is talking to the mock service worker. Defaults to
   * `VITE_ENABLE_MSW=1` in a dev build — the same condition under which
   * `main.tsx` starts the worker, so a production bundle (never `DEV`) always
   * gets the production backoff whatever its environment says.
   */
  readonly mockBackend?: boolean;
}

/**
 * One query client for the application.
 *
 * Two decisions are load-bearing:
 *
 *  - **Retry policy.** TanStack Query's default retries everything three
 *    times, which would silently re-send a decision the server refused on
 *    purpose. A query is retried only when the typed failure says it is
 *    retriable, and a mutation is never retried automatically at all — a
 *    mutation is an external effect, and "did that land?" is not a question a
 *    client may answer by guessing (design doc §14.4, §22.4).
 *
 *  - **`WORKSPACE_PAUSED` is evidence.** Whatever request meets it, the kill
 *    switch is on; the global pause banner must appear everywhere, not only on
 *    the screen that happened to ask (design doc §22.3).
 */
export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  const mockBackend = options.mockBackend ?? (env.mswEnabled && import.meta.env.DEV);
  // Declared before the client so the cache callbacks can reach it, and
  // assigned once — the cycle is real: the caches need the client, the client
  // needs the caches.
  // eslint-disable-next-line prefer-const
  let client: QueryClient;

  const observePause = (error: unknown) => {
    const failure = toFailure(error);
    if (!failure || failure.kind !== 'api' || failure.code !== 'WORKSPACE_PAUSED') return;
    const workspaceId = failure.details['workspace_id'];
    if (typeof workspaceId !== 'string') return;
    recordPauseObservation(client, workspaceId, {
      paused: true,
      pauseEpoch: null,
      note: failure.message,
    });
  };

  client = new QueryClient({
    queryCache: new QueryCache({ onError: observePause }),
    mutationCache: new MutationCache({ onError: observePause }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          const failure = toFailure(error);
          if (!failure) return false;
          return failure.retriable && failureCount < 2;
        },
        retryDelay: honouringRetryAfter(mockBackend ? mockBackendRetryDelay : productionRetryDelay),
      },
      mutations: { retry: false },
    },
  });

  return client;
}
